import { useMemo } from 'react';
import { Users, Zap, FolderOpen, ListTodo, Plus, Sparkles } from '../../../../icons';
import type { MemberState, TeamMemberAttendanceDto } from '../../../../api/hr-team';
import type { NodePublic } from '../../../../api/workspace';
import { useAuthStore } from '../../../../store/authStore';
import { useMenuGate, useMenuEnabledFn } from '../../../../lib/menu-gate';
import { WidgetCard } from '../WidgetCard';
import { useWidgetChrome } from '../widget-chrome';
import { useWidgetActions } from '../widget-context';
import { QUICK_LINKS, DEFAULT_QUICK_LINKS, getQuickLink } from '../quick-links';
import { agoText } from '../relative-time';
import { boxPath } from '../../../ApprovalPage/box/approval-box-types';
import {
  useTeamPresence, useRecentDocs, useIncomingApprovals, useMyTasks, useMonthEvents,
  useCompletedApprovals, useMonthDailyWork,
} from '../widget-queries';
// 대시보드 시안 v2 V3b (2026-09-16) — 팀 근무 현황 · 오늘 할 일 · 이번 주 요약이 보이는 항목을 읽는 판(아래 끝).
//   깃발이 꺼져 있으면 옛 판 그대로다.
import { DASHBOARD_V2_ENABLED } from '../../../../lib/layout-shell';
import { Gauge } from '../../../../components/shared/Gauge';
import { useWidgetItems } from '../use-widget-items';
import { useDailyWorkMonth, useMailFolder } from '../widget-queries';
import {
  addDays, eventMinutes, inRange, previousRange, signed, touchesMonth, uniqueById,
  weekStart as weekStartV2, type Range,
} from './v2/period';
import './v2/widgets-v2.css';

/**
 * 업무 위젯 — 시안 화면 L 의 「제안」 넷 (2026-09-09 · 판 5).
 *
 * 화면 L 의 ①~④(내 정보 · 휴가 · 근태 · 메일)는 **이미 있는 위젯**이다 — 여기서 다시 만들지 않는다.
 */

/* ── ⑤ 팀 근무 현황 ─────────────────────────────────────────────────────── */

/**
 * 근무 상태의 이름 — **근태 화면과 같은 낱말을 쓴다.**
 *
 * ★★★ 같은 것을 두 이름으로 부르면 사람들은 다른 것으로 읽는다(「대체휴무 / 대체휴가」로 겪었다).
 *     여기 다섯은 `TeamAttendancePage` 의 배지와 같은 말이다.
 */
const STATE_META: Record<MemberState, { label: string; color: string }> = {
  WORKING: { label: '근무 중', color: 'var(--color-success)' },
  DEEMED: { label: '출장 · 외근', color: 'var(--accent-orange)' },
  OFF: { label: '퇴근', color: 'var(--text-subtle)' },
  LEAVE: { label: '휴가', color: 'var(--accent-blue)' },
  ABSENT: { label: '미출근', color: 'var(--color-danger)' },
};

const STATE_ORDER: MemberState[] = ['WORKING', 'DEEMED', 'LEAVE', 'OFF', 'ABSENT'];

/**
 * 팀 근무 현황 위젯 (시안 화면 L ⑤).
 *
 * ★★★ **볼 자격이 아닌 것은 「조회 실패」가 아니다.** 이 API 는 대표 · 부서장 · 팀장 · 근태
 *     담당자에게만 열려 있다(그 밖에는 403). 실패로 적으면 「고장 났나」로 읽히므로,
 *     **누가 볼 수 있는지**를 그대로 적는다.
 * ★ 사람 목록은 «상태가 궁금한 순서»로 세운다 — 근무 중 · 출장 · 휴가 · 퇴근 · 미출근.
 */
export function TeamPresenceWidget() {
  return DASHBOARD_V2_ENABLED ? <TeamPresenceWidgetV2 /> : <TeamPresenceWidgetLegacy />;
}

function TeamPresenceWidgetLegacy() {
  const { go } = useWidgetActions();
  const gate = useMenuGate('attendance', '근태');
  const { data, loading, error } = useTeamPresence(gate.enabled);

  const members: TeamMemberAttendanceDto[] = useMemo(() => {
    const rows = data?.members ?? [];
    return rows.slice().sort((a, b) => {
      const d = STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state);
      return d !== 0 ? d : a.name.localeCompare(b.name, 'ko');
    });
  }, [data]);

  const counts = useMemo(() => {
    const map = new Map<MemberState, number>();
    for (const m of members) map.set(m.state, (map.get(m.state) ?? 0) + 1);
    return STATE_ORDER.filter((s) => (map.get(s) ?? 0) > 0).map((s) => ({ state: s, n: map.get(s) ?? 0 }));
  }, [members]);

  return (
    <WidgetCard
      title="팀 근무 현황"
      icon={Users}
      tail={data ? `${members.length}명` : null}
      link={data ? { label: '근태 현황', onClick: () => go('/erp/attendance/team') } : undefined}
      loading={loading}
      error={error}
      empty={!loading && !error && !data
        ? '대표 · 부서장 · 팀장만 볼 수 있습니다'
        : !loading && !error && members.length === 0 ? '오늘 볼 기록이 없습니다' : null}
      menuOff={gate.enabled ? null : gate.label}
    >
      <div className="wi-team">
        <div className="wi-team__sum">
          {counts.map(({ state, n }) => (
            <span key={state} className="wi-team__chip">
              <i style={{ background: STATE_META[state].color }} aria-hidden />
              {STATE_META[state].label} <b>{n}</b>
            </span>
          ))}
        </div>
        <div className="wi-team__list">
          {members.slice(0, 6).map((m) => (
            <span key={m.userId} className="wi-team__p">
              <span className="wi-team__av" aria-hidden>{m.name.slice(0, 1)}</span>
              <span className="wi-team__n">{m.name}</span>
              <span className="wi-team__s" style={{ color: STATE_META[m.state].color }}>
                {STATE_META[m.state].label}
              </span>
            </span>
          ))}
        </div>
      </div>
    </WidgetCard>
  );
}

/* ── ⑥ 빠른 실행 ────────────────────────────────────────────────────────── */

/**
 * 빠른 실행 위젯 (시안 화면 L ⑥).
 *
 * ★★★ **무엇을 놓을지는 사용자가 고른다**(`quick-links.tsx` 의 카탈로그). 고정 여덟으로 두면
 *     절반은 아무도 안 누른다.
 * ★★ **끈 메뉴의 바로가기는 빠진다** — 못 가는 곳을 눌러 보게 하는 것은 잘못된 안내다.
 * ★ 「추가」 타일은 **고칠 수 있는 보드에서만** 뜬다(잠긴 전사 보드에서는 눌러도 할 일이 없다).
 */
export function QuickLaunchWidget() {
  const actions = useWidgetActions();
  const chrome = useWidgetChrome();
  const menuEnabled = useMenuEnabledFn();

  const picked = chrome.settings.quickLinks ?? DEFAULT_QUICK_LINKS;
  const links = picked
    .map((k) => getQuickLink(k))
    .filter((d): d is (typeof QUICK_LINKS)[number] => d !== undefined)
    .filter((d) => !d.menuKey || menuEnabled(d.menuKey));

  return (
    <WidgetCard
      title="빠른 실행"
      icon={Zap}
      /*
       * 머리 곁말 (2026-09-16) — 몇 개를 담았는지와 「바꾸기」 입구를 제목 옆에 둔다.
       * ★★ 세는 것은 **실제로 그려진 수**(`links`)다. 카탈로그 기본값 여덟이나 고른 수(`picked`)를
       *    적으면, 끈 메뉴로 빠진 타일 때문에 «적힌 수와 보이는 타일이 어긋난다».
       * ★ 격자 안 「추가」 타일은 그대로 둔다 — 입구가 둘이어도 해롭지 않고, 제목을 숨긴
       *   보드(`showTitle` 꺼짐)에서는 이 곁말이 통째로 사라지므로 그쪽이 유일한 입구가 된다.
       */
      tail={(
        <>
          바로가기 {links.length}개
          {chrome.openSettings && (
            <button type="button" className="wi-quick__edit" onClick={chrome.openSettings}>바꾸기</button>
          )}
        </>
      )}
      empty={links.length === 0 && !chrome.openSettings ? '고른 바로가기가 없습니다' : null}
    >
      <div className="wi-quick">
        {links.map((d) => (
          <button type="button" key={d.key} className="wi-quick__b" onClick={() => d.run(actions)}>
            <d.icon size={18} aria-hidden />
            {d.label}
          </button>
        ))}
        {chrome.openSettings && (
          <button
            type="button" className="wi-quick__b wi-quick__b--add"
            onClick={chrome.openSettings}
          >
            <Plus size={18} aria-hidden />
            추가
          </button>
        )}
      </div>
    </WidgetCard>
  );
}

/* ── ⑦ 최근 문서 ────────────────────────────────────────────────────────── */

/** 확장자에서 유형 글자 하나 — 시안의 X · W · P · H. 모르는 것은 확장자 첫 글자. */
function docGlyph(name: string): { ch: string; cls: string } {
  const ext = (name.split('.').pop() ?? '').toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) return { ch: 'X', cls: 'wi-doc__i--x' };
  if (['docx', 'doc', 'rtf'].includes(ext)) return { ch: 'W', cls: 'wi-doc__i--w' };
  if (['pptx', 'ppt'].includes(ext)) return { ch: 'P', cls: 'wi-doc__i--p' };
  if (['hwp', 'hwpx'].includes(ext)) return { ch: 'H', cls: 'wi-doc__i--h' };
  if (ext === 'pdf') return { ch: 'PDF', cls: 'wi-doc__i--pdf' };
  return { ch: (ext.slice(0, 1) || '?').toUpperCase(), cls: '' };
}

// 상대 시각은 board/relative-time.ts 한 곳에서 만든다 (2026-09-22).
// ⚠ 이 위젯은 종전에 「09-15」로 적었다 — 모으면서 「9/15」로 바뀐다(같은 판의 다른 위젯들과 같은 말).

/**
 * 최근 문서 위젯 (시안 화면 L ⑦).
 *
 * ★ 워크스페이스의 「최근 수정」(scope=home)을 그대로 본다 — **접근 권한은 서버가 이미 지킨다.**
 */
export function RecentDocsWidget() {
  const { go } = useWidgetActions();
  const gate = useMenuGate('workspace', '워크스페이스');
  const { data, loading, error } = useRecentDocs(gate.enabled);
  const rows: NodePublic[] = data ?? [];

  return (
    <WidgetCard
      title="최근 문서"
      icon={FolderOpen}
      link={{ label: '워크스페이스', onClick: () => go('/workspace') }}
      loading={loading}
      error={error}
      empty={!loading && !error && rows.length === 0 ? '최근에 연 문서가 없습니다' : null}
      menuOff={gate.enabled ? null : gate.label}
    >
      <div className="wi-docs">
        {rows.slice(0, 4).map((n) => {
          const name = n.docTitle || n.name;
          const g = docGlyph(n.name);
          return (
            <button type="button" key={n.id} className="wi-doc" onClick={() => go('/workspace')} title={name}>
              <span className={`wi-doc__i ${g.cls}`} aria-hidden>{g.ch}</span>
              <span className="wi-doc__n">{name}</span>
              <span className="wi-doc__t">{agoText(n.updatedAt ?? n.createdAt)}</span>
            </button>
          );
        })}
      </div>
    </WidgetCard>
  );
}

/* ── ⑧ 오늘 할 일 ───────────────────────────────────────────────────────── */

interface TodoRow { key: string; text: string; tag: string; tone: '' | 'g' | 'o'; to: string }

function isToday(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

/**
 * 오늘 할 일 위젯 (시안 화면 L ⑧).
 *
 * ★★★ **결재 · 태스크 · 일정을 한 줄로 모은다** — 어느 화면에 들어가야 할지 모르는 채로 하루를
 *     시작하지 않게. 없는 것은 **줄 자체가 없다**(0건 줄을 세우면 할 일이 있는 것처럼 읽힌다).
 * ⚠ 시안의 「보고 · 설문」은 아직 그 도메인이 없다 — 지어내지 않는다.
 */
export function TodayTodoWidget() {
  return DASHBOARD_V2_ENABLED ? <TodayTodoWidgetV2 /> : <TodayTodoWidgetLegacy />;
}

function TodayTodoWidgetLegacy() {
  const { go } = useWidgetActions();
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const apprGate = useMenuGate('approval', '전자결재');
  const taskGate = useMenuGate('task', '할일');
  const calGate = useMenuGate('calendar', '캘린더');

  const approvals = useIncomingApprovals(apprGate.enabled);
  const tasks = useMyTasks(myId, taskGate.enabled);
  const now = new Date();
  const events = useMonthEvents(now.getFullYear(), now.getMonth(), calGate.enabled);

  const rows = useMemo<TodoRow[]>(() => {
    const out: TodoRow[] = [];

    const waiting = (approvals.data ?? []).length;
    if (waiting > 0) {
      out.push({ key: 'approval', text: `결재할 문서 ${waiting}건`, tag: '결재', tone: 'o', to: '/approval/pending' });
    }

    const today = (tasks.data ?? []).filter((t) => t.dueDate && isToday(t.dueDate));
    if (today.length > 0) {
      out.push({ key: 'task-today', text: `오늘 마감 태스크 ${today.length}건`, tag: '마감', tone: 'o', to: '/project/my-tasks' });
    }
    const overdue = (tasks.data ?? []).filter((t) => t.dueDate && new Date(t.dueDate) < new Date(now.toDateString()));
    if (overdue.length > 0) {
      out.push({ key: 'task-over', text: `기한 지난 태스크 ${overdue.length}건`, tag: '지남', tone: 'o', to: '/project/my-tasks' });
    }

    const todayEvents = (events.data ?? []).filter((e) => isToday(e.startAt));
    const next = todayEvents.find((e) => new Date(e.startAt).getTime() >= Date.now()) ?? todayEvents[0];
    if (next) {
      const t = new Date(next.startAt);
      out.push({
        key: 'event',
        text: `오늘 일정 ${todayEvents.length}건 · 다음 ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')} ${next.title}`,
        tag: '일정', tone: 'g', to: '/calendar',
      });
    }
    return out;
  }, [approvals.data, tasks.data, events.data]);

  const loading = approvals.loading || tasks.loading || events.loading;

  return (
    <WidgetCard
      title="오늘 할 일"
      icon={ListTodo}
      tail={rows.length > 0 ? `${rows.length}줄` : null}
      loading={loading && rows.length === 0}
      empty={!loading && rows.length === 0 ? '오늘 급한 일이 없습니다' : null}
    >
      <div className="wi-todo">
        {rows.map((r) => (
          <button type="button" key={r.key} className="wi-todo__i" onClick={() => go(r.to)}>
            <i className={`wi-todo__bar${r.tone ? ` wi-todo__bar--${r.tone}` : ''}`} aria-hidden />
            <span className="wi-todo__t">{r.text}</span>
            <span className="wi-todo__tag">{r.tag}</span>
          </button>
        ))}
      </div>
    </WidgetCard>
  );
}

/* ── ⑨ 이번 주 요약 (시안 화면 A) ───────────────────────────────────────── */

/** 그 주(월~일)의 시작. 일요일이면 지난 월요일로 당긴다 — 타임라인과 같은 셈. */
function weekStartOf(base: Date): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

const H = (min: number) => `${(min / 60).toFixed(min % 60 === 0 ? 0 : 1)}시간`;

/**
 * 이번 주 요약 위젯 (시안 화면 A).
 *
 * ★★★ **시안의 「집중 시간」은 만들지 않았다.** 그것을 셀 데이터가 이 시스템에 없다 —
 *     자리를 채우려고 아무 숫자나 넣으면 그 위젯은 다시는 안 믿긴다. 대신 **실제로 아는 것**
 *     셋을 센다: 회의(캘린더) · 근무(근태 날별) · 처리한 결재.
 * ★★★ **모르는 값은 「—」다.** 옛 서버라 날별 집계가 없거나 메뉴가 꺼져 있으면 그 줄은
 *     아예 세우지 않는다(0 으로 적으면 「일을 안 했다」로 읽힌다).
 * ★★ 「회의 비중」은 **근무 시간이 있어야** 뜻이 있다 — 없으면 막대도 안 그린다.
 * ★ 지난주 비교는 **같은 달 안에서 셀 수 있을 때만** 적는다(달을 두 번 받지 않는다).
 */
export function WeekSummaryWidget() {
  return DASHBOARD_V2_ENABLED ? <WeekSummaryWidgetV2 /> : <WeekSummaryWidgetLegacy />;
}

function WeekSummaryWidgetLegacy() {
  const { go } = useWidgetActions();
  const calGate = useMenuGate('calendar', '캘린더');
  const attGate = useMenuGate('attendance', '근태');
  const apprGate = useMenuGate('approval', '전자결재');

  const now = new Date();
  const events = useMonthEvents(now.getFullYear(), now.getMonth(), calGate.enabled);
  const daily = useMonthDailyWork(attGate.enabled);
  const done = useCompletedApprovals(apprGate.enabled);

  const sum = useMemo(() => {
    const from = weekStartOf(now);
    const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 7);
    const prevFrom = new Date(from.getFullYear(), from.getMonth(), from.getDate() - 7);

    const meetingIn = (a: Date, b: Date) => (events.data ?? [])
      .filter((e) => !e.isAllDay)
      .filter((e) => { const t = new Date(e.startAt); return t >= a && t < b; })
      .reduce((n, e) => n + Math.max(0, (new Date(e.endAt).getTime() - new Date(e.startAt).getTime()) / 60000), 0);

    const meeting = events.data ? meetingIn(from, to) : null;
    /* ★ 지난주가 이 달 안에 있을 때만 센다 — 달을 한 번 더 받아야 하는 값은 «모른다». */
    const prevMeeting = events.data && prevFrom.getMonth() === now.getMonth() ? meetingIn(prevFrom, from) : null;

    const worked = daily.data
      ? (daily.data.days ?? [])
        .filter((d) => {
          const t = new Date(`${d.date}T00:00:00`);
          return t >= from && t < to;
        })
        .reduce((n, d) => n + d.creditedMinutes, 0)
      : null;

    const approvals = done.data
      ? done.data.items.filter((d) => {
        const t = new Date(d.completedAt ?? d.draftedAt ?? d.createdAt);
        return t >= from && t < to;
      }).length
      : null;

    return { from, to, meeting, prevMeeting, worked, approvals };
  }, [events.data, daily.data, done.data]);

  const rows: Array<{ k: string; v: string; to?: string }> = [];
  if (calGate.enabled) rows.push({ k: '회의', v: sum.meeting == null ? '—' : H(sum.meeting), to: '/calendar' });
  if (attGate.enabled) rows.push({ k: '근무', v: sum.worked == null ? '—' : H(sum.worked), to: '/erp/attendance' });
  // ★ 처리한(끝낸) 결재라 **완료함**으로 간다 (2026-09-15) — 종전 `/approval/pending` 은 결재 대기였다.
  if (apprGate.enabled) rows.push({ k: '처리한 결재', v: sum.approvals == null ? '—' : `${sum.approvals}건`, to: boxPath('done') });

  /* 회의 비중 — 근무 시간이 있어야 뜻이 있다. 둘 다 알 때만 그린다. */
  const share = sum.meeting != null && sum.worked != null && sum.worked > 0
    ? Math.min(100, Math.round((sum.meeting / sum.worked) * 100))
    : null;
  const prevShare = sum.prevMeeting != null && sum.worked != null && sum.worked > 0
    ? Math.min(100, Math.round((sum.prevMeeting / sum.worked) * 100))
    : null;

  const range = `${sum.from.getMonth() + 1}/${sum.from.getDate()} ~ ${sum.to.getMonth() + 1}/${sum.to.getDate() - 1 || sum.to.getDate()}`;

  return (
    <WidgetCard
      title="이번 주 요약"
      icon={Sparkles}
      tail={range}
      loading={events.loading && daily.loading}
      empty={rows.length === 0 ? '보여 줄 것이 없습니다 — 메뉴가 모두 꺼져 있습니다' : null}
    >
      <div className="wi-sum">
        {rows.map((r) => (
          <button type="button" key={r.k} className="wi-sum__i" onClick={() => r.to && go(r.to)}>
            <span className="wi-sum__k">{r.k}</span>
            <span className="wi-sum__v">{r.v}</span>
          </button>
        ))}

        {share != null && (
          <>
            <span className="wi-sum__track" aria-hidden><i style={{ width: `${share}%` }} /></span>
            <span className="wi-sum__legend">
              <span>회의 비중 {share}%</span>
              {/* ★ 지난주를 못 세면 «모른다» 고 적는다 — 빈칸으로 두면 「같다」로 읽힌다. */}
              <span>지난주 {prevShare != null ? `${prevShare}%` : '모름'}</span>
            </span>
          </>
        )}
      </div>
    </WidgetCard>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   대시보드 시안 v2 V3b (2026-09-16) — 보이는 항목 · 선택지를 읽는 판 셋 (깃발 켤 때만)
   ★★★ 설정이 없으면 옛 판과 같은 모양이다(V1 규칙 D1) — 예외: 이번 주 요약의 회의 비중은 게이지(D-Q4)이고,
       「지난주와 비교」가 줄마다 배지를 붙인다(시안 · 항목 설명 「각 줄 오른쪽 배지」).
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * 팀 근무 현황 v2 — 표시 인원 · 보일 상태 · 상태 칩 · 사람 목록 · 미출근만 먼저.
 * ★ 범위는 서버 기본값 그대로다 — 팀 · 부서 · 전사를 고르는 것은 서버가 볼 수 있는 범위를 알려 준 뒤(DR6 · DR7).
 */
export function TeamPresenceWidgetV2() {
  const { go } = useWidgetActions();
  const gate = useMenuGate('attendance', '근태');
  const items = useWidgetItems('team-presence');
  const { data, loading, error } = useTeamPresence(gate.enabled);
  const count = Number(items.option<string>('count')) || 6;
  const statusKey = (items.option<string[]>('statuses') ?? STATE_ORDER).join(',');
  const absentFirst = items.has('absentFirst');
  const showCounts = items.has('counts');
  const showList = items.has('list');

  const members: TeamMemberAttendanceDto[] = useMemo(() => {
    const allowed = new Set(statusKey.split(','));
    return (data?.members ?? []).filter((m) => allowed.has(m.state)).slice().sort((a, b) => {
      if (absentFirst) {
        const x = Number(b.state === 'ABSENT') - Number(a.state === 'ABSENT');
        if (x !== 0) return x;
      }
      const d = STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state);
      return d !== 0 ? d : a.name.localeCompare(b.name, 'ko');
    });
  }, [data, statusKey, absentFirst]);

  const counts = useMemo(() => {
    const map = new Map<MemberState, number>();
    for (const m of members) map.set(m.state, (map.get(m.state) ?? 0) + 1);
    return STATE_ORDER.filter((s) => (map.get(s) ?? 0) > 0).map((s) => ({ state: s, n: map.get(s) ?? 0 }));
  }, [members]);
  const total = data?.members?.length ?? 0;

  return (
    <WidgetCard
      title="팀 근무 현황"
      icon={Users}
      tail={data ? `${total}명` : null}
      link={data ? { label: '근태 현황', onClick: () => go('/erp/attendance/team') } : undefined}
      loading={loading}
      error={error}
      empty={!loading && !error && !data
        ? '대표 · 부서장 · 팀장만 볼 수 있습니다'
        : !loading && !error && members.length === 0
          ? (total === 0 ? '오늘 볼 기록이 없습니다' : '고른 상태의 사람이 없습니다')
          : null}
      menuOff={gate.enabled ? null : gate.label}
    >
      <div className="wi-team">
        {showCounts && (
          <div className="wi-team__sum">
            {counts.map(({ state, n }) => (
              <span key={state} className="wi-team__chip">
                <i style={{ background: STATE_META[state].color }} aria-hidden />
                {STATE_META[state].label} <b>{n}</b>
              </span>
            ))}
          </div>
        )}
        {showList && (
          <div className="wi-team__list">
            {members.slice(0, count).map((m) => (
              <span key={m.userId} className="wi-team__p">
                <span className="wi-team__av" aria-hidden>{m.name.slice(0, 1)}</span>
                <span className="wi-team__n">{m.name}</span>
                <span className="wi-team__s" style={{ color: STATE_META[m.state].color }}>{STATE_META[m.state].label}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </WidgetCard>
  );
}

/**
 * 오늘 할 일 v2 — 보이는 항목 6 · 순서 · 0건 줄 숨기기.
 * ★★ 0건 줄은 **값을 알 때만** 세운다 — 못 읽은 것(메뉴 꺼짐 · 실패 · 아직)은 0 이 아니다.
 * ★ 켜지 않은 항목의 데이터는 부르지 않는다 · 확인 요청 공지는 서버(게시판 R8) 전이라 설정에서 막혀 있다.
 */
export function TodayTodoWidgetV2() {
  const { go } = useWidgetActions();
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const items = useWidgetItems('today-todo');
  const order = items.visible('items');
  const orderKey = order.join(',');
  const hideEmpty = items.option<boolean>('hideEmpty') !== false;
  const apprGate = useMenuGate('approval', '전자결재');
  const taskGate = useMenuGate('task', '할일');
  const calGate = useMenuGate('calendar', '캘린더');
  const mailGate = useMenuGate('mail', '메일');

  const approvals = useIncomingApprovals(apprGate.enabled && order.includes('approval'));
  const tasks = useMyTasks(myId, taskGate.enabled && (order.includes('taskToday') || order.includes('taskOverdue')));
  const now = new Date();
  const events = useMonthEvents(now.getFullYear(), now.getMonth(), calGate.enabled && order.includes('event'));
  const mail = useMailFolder('inbox', mailGate.enabled && order.includes('unreadMail'));

  const rows = useMemo<TodoRow[]>(() => {
    const out: TodoRow[] = [];
    const push = (n: number | null, row: TodoRow) => {
      if (n === null) return;
      if (n === 0 && hideEmpty) return;
      out.push(row);
    };
    const today0 = new Date(new Date().toDateString());
    for (const key of orderKey.split(',').filter(Boolean)) {
      if (key === 'approval') {
        const n = approvals.data ? approvals.data.length : null;
        push(n, { key, text: `결재할 문서 ${n ?? 0}건`, tag: '결재', tone: 'o', to: '/approval/pending' });
      } else if (key === 'taskToday') {
        const n = tasks.data ? tasks.data.filter((t) => t.dueDate && isToday(t.dueDate)).length : null;
        push(n, { key, text: `오늘 마감 태스크 ${n ?? 0}건`, tag: '마감', tone: 'o', to: '/project/my-tasks' });
      } else if (key === 'taskOverdue') {
        const n = tasks.data ? tasks.data.filter((t) => t.dueDate && new Date(t.dueDate) < today0).length : null;
        push(n, { key, text: `기한 지난 태스크 ${n ?? 0}건`, tag: '지남', tone: 'o', to: '/project/my-tasks' });
      } else if (key === 'event') {
        if (!events.data) continue;
        const todayEvents = events.data.filter((e) => isToday(e.startAt));
        const next = todayEvents.find((e) => new Date(e.startAt).getTime() >= Date.now()) ?? todayEvents[0];
        if (next) {
          const t = new Date(next.startAt);
          out.push({
            key,
            text: `오늘 일정 ${todayEvents.length}건 · 다음 ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')} ${next.title}`,
            tag: '일정', tone: 'g', to: '/calendar',
          });
        } else {
          push(0, { key, text: '오늘 일정 0건', tag: '일정', tone: 'g', to: '/calendar' });
        }
      } else if (key === 'unreadMail') {
        const n = mail.data ? mail.data.filter((m) => !m.isRead).length : null;
        push(n, { key, text: `안 읽은 메일 ${n ?? 0}통`, tag: '메일', tone: 'g', to: '/mail' });
      }
    }
    return out;
  }, [approvals.data, tasks.data, events.data, mail.data, orderKey, hideEmpty]);

  const loading = approvals.loading || tasks.loading || events.loading || mail.loading;

  return (
    <WidgetCard
      title="오늘 할 일"
      icon={ListTodo}
      tail={rows.length > 0 ? `${rows.length}줄` : null}
      loading={loading && rows.length === 0}
      empty={!loading && rows.length === 0 ? '오늘 급한 일이 없습니다' : null}
    >
      <div className="wi-todo">
        {rows.map((r) => (
          <button type="button" key={r.key} className="wi-todo__i" onClick={() => go(r.to)}>
            <i className={`wi-todo__bar${r.tone ? ` wi-todo__bar--${r.tone}` : ''}`} aria-hidden />
            <span className="wi-todo__t">{r.text}</span>
            <span className="wi-todo__tag">{r.tag}</span>
          </button>
        ))}
      </div>
    </WidgetCard>
  );
}

/**
 * 이번 주 요약 v2 — 보이는 항목 6 · 순서 · 기간(이번 주 · 지난주) · 줄마다 비교 배지 · 회의 비중 게이지.
 *
 * ★★★ **회의 = `type === 'meeting'` 인 일정만**(관제 결정 Q-10) — 옛 판은 종일이 아닌 모든 일정을 셌다.
 * ★★ 처리한 결재는 옛 판과 같은 셈이다(끝낸 결재 100건에서 그 주에 끝난 것). `stats/me` 의 `week` 는
 *    «오늘부터 7일 · 올린 날» 기준이라 달력 주 · 끝낸 날과 뜻이 달라 쓰지 않는다. 100건에서 잘리면 비교하지 않는다.
 * ★★ 값을 모르면 「—」 · 비교는 두 주를 다 알 때만 · 게이지는 근무 시간을 알아야 선다(옛 판과 같음).
 * ★ 주가 옆 달에 걸칠 때만 그 달 일정 · 근무를 더 받는다. 집중 시간 · 팀 합계는 서버 전이라 설정에서 막혀 있다.
 */
export function WeekSummaryWidgetV2() {
  const { go } = useWidgetActions();
  const items = useWidgetItems('week-summary');
  const order = items.visible('items');
  const lastWeek = items.option<string>('period') === 'lastWeek';
  const showCompare = order.includes('compare');
  const calGate = useMenuGate('calendar', '캘린더');
  const attGate = useMenuGate('attendance', '근태');
  const apprGate = useMenuGate('approval', '전자결재');

  const now = new Date();
  const thisWeek: Range = { from: weekStartV2(now), to: addDays(weekStartV2(now), 7) };
  const cur = lastWeek ? previousRange('week', thisWeek) : thisWeek;
  const prev = previousRange('week', cur);
  const y = now.getFullYear();
  const m = now.getMonth();
  const pm = new Date(y, m - 1, 1);
  const nm = new Date(y, m + 1, 1);
  const needPrev = [cur, prev].some((r) => touchesMonth(r, pm.getFullYear(), pm.getMonth()));
  const needNext = [cur, prev].some((r) => touchesMonth(r, nm.getFullYear(), nm.getMonth()));

  const evCur = useMonthEvents(y, m, calGate.enabled);
  const evPrev = useMonthEvents(pm.getFullYear(), pm.getMonth(), calGate.enabled && needPrev);
  const evNext = useMonthEvents(nm.getFullYear(), nm.getMonth(), calGate.enabled && needNext);
  const dwCur = useDailyWorkMonth(y, m + 1, attGate.enabled);
  const dwPrev = useDailyWorkMonth(pm.getFullYear(), pm.getMonth() + 1, attGate.enabled && needPrev);
  const done = useCompletedApprovals(apprGate.enabled);

  const sum = useMemo(() => {
    const eventsKnown = Boolean(evCur.data) && (!needPrev || Boolean(evPrev.data)) && (!needNext || Boolean(evNext.data));
    const meetings = uniqueById([...(evCur.data ?? []), ...(evPrev.data ?? []), ...(evNext.data ?? [])])
      .filter((e) => e.type === 'meeting');
    const meeting = (r: Range) => (eventsKnown
      ? meetings.filter((e) => inRange(e.startAt, r)).reduce((s, e) => s + eventMinutes(e), 0)
      : null);
    const workKnown = dwCur.data != null && (!needPrev || dwPrev.data != null);
    const days = [...(dwCur.data?.days ?? []), ...(dwPrev.data?.days ?? [])];
    const worked = (r: Range) => (workKnown
      ? days.filter((d) => inRange(`${d.date}T00:00:00`, r)).reduce((s, d) => s + d.creditedMinutes, 0)
      : null);
    const approvals = (r: Range) => (done.data
      ? done.data.items.filter((d) => inRange(d.completedAt ?? d.draftedAt ?? d.createdAt, r)).length
      : null);
    return {
      meeting: meeting(cur), prevMeeting: meeting(prev),
      worked: worked(cur), prevWorked: worked(prev),
      approvals: approvals(cur), prevApprovals: approvals(prev),
      truncated: Boolean(done.data?.truncated),
    };
    // cur · prev 는 lastWeek 로만 정해진다(오늘 안에서).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evCur.data, evPrev.data, evNext.data, dwCur.data, dwPrev.data, done.data, lastWeek, needPrev, needNext]);

  const prevLabel = lastWeek ? '그 전 주' : '지난주';
  const diffHours = (a: number | null, b: number | null) =>
    (showCompare && a != null && b != null ? signed((a - b) / 60, 1, 'h') : null);
  const share = (mt: number | null, wk: number | null) =>
    (mt != null && wk != null && wk > 0 ? Math.min(100, Math.round((mt / wk) * 100)) : null);
  const curShare = share(sum.meeting, sum.worked);
  const prevShare = share(sum.prevMeeting, sum.prevWorked);

  const row = (key: string, k: string, v: string, cmp: string | null, to: string) => (
    <button type="button" key={key} className="wi-sum__i" onClick={() => go(to)}>
      <span className="wi-sum__k">{k}</span>
      <span className="wi-sum__v">{v}</span>
      {cmp && <span className="wv2-meta">{prevLabel}보다 {cmp}</span>}
    </button>
  );

  const parts: JSX.Element[] = [];
  for (const key of order) {
    if (key === 'meeting') {
      parts.push(row(key, '회의', sum.meeting == null ? '—' : H(sum.meeting), diffHours(sum.meeting, sum.prevMeeting), '/calendar'));
    } else if (key === 'worked') {
      parts.push(row(key, '근무', sum.worked == null ? '—' : H(sum.worked), diffHours(sum.worked, sum.prevWorked), '/erp/attendance'));
    } else if (key === 'approvals') {
      const cmp = showCompare && !sum.truncated && sum.approvals != null && sum.prevApprovals != null
        ? signed(sum.approvals - sum.prevApprovals) : null;
      /*
       * ★★★ **완료함**으로 간다 (2026-09-16 고침). 바로 위 옛 판(`:394`)에는 같은 고침이
       *     주석과 함께 이미 있었다 — 「처리한(끝낸) 결재라 완료함으로 간다 · 종전 `/approval/pending`
       *     은 결재 대기였다」(2026-09-15). **그 고침이 V2 로 옮겨지지 않아 같은 결함이 되살아났다.**
       */
      parts.push(row(key, '처리한 결재', sum.approvals == null ? '—' : `${sum.approvals}건`, cmp, boxPath('done')));
    } else if (key === 'share' && curShare != null) {
      parts.push(
        <Gauge
          key="share"
          value={curShare}
          label="회의 비중"
          caption={showCompare && prevShare != null ? `${prevLabel} ${prevShare}%` : undefined}
        />,
      );
    }
  }

  const last = addDays(cur.to, -1);
  const range = `${cur.from.getMonth() + 1}/${cur.from.getDate()} ~ ${last.getMonth() + 1}/${last.getDate()}`;

  return (
    <WidgetCard
      title="이번 주 요약"
      icon={Sparkles}
      tail={range}
      loading={evCur.loading && dwCur.loading}
      empty={parts.length === 0 ? '보여 줄 것이 없습니다 — 메뉴가 꺼졌거나 모두 껐습니다' : null}
    >
      <div className="wi-sum">{parts}</div>
    </WidgetCard>
  );
}
